<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import useReaderStore from '../../stores/reader';
import useLocale from '../../composables/useLocale';
import useNavigationStore from '../../stores/navigation';
import { NAVIGATION_ITEMS, type NavigationItem } from '../../types';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown, faChevronLeft } from '@fortawesome/pro-solid-svg-icons';

library.add([faChevronDown, faChevronLeft]);

type Emits = {
  (e: 'closeTableOfContent'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  navigationItems?: NavigationItem[];
};
const props = defineProps<Props>();

const { t } = useLocale();
const { smAndDown: isMobile } = useDisplay();

const { hasBookmarks, hasHighlights, isTocBottom, isAudioOnly } = storeToRefs(useReaderStore());
const { hasPages } = storeToRefs(useNavigationStore());

const search = ref('');
const tab = ref(0);

const filteredNavItems = computed<NavigationItem[]>(() => {
  const items = props.navigationItems ?? [];

  return items.filter((item) => {
    if (item === NAVIGATION_ITEMS.PAGE_LIST) return hasPages.value;
    if (item === NAVIGATION_ITEMS.BOOKMARKS_LIST) return hasBookmarks.value;
    if (item === NAVIGATION_ITEMS.HIGHLIGHT_LIST) return hasHighlights.value;
    return true;
  });
});

const getTabTitle = (item: NavigationItem): string => {
  const suffixMap: Record<NavigationItem, string> = {
    [NAVIGATION_ITEMS.PAGE_LIST]: 'pages',
    [NAVIGATION_ITEMS.BOOKMARKS_LIST]: 'bookmarks',
    [NAVIGATION_ITEMS.TABLE_OF_CONTENT]: 'table_of_contents',
    [NAVIGATION_ITEMS.HIGHLIGHT_LIST]: 'highlights',
    [NAVIGATION_ITEMS.PLAYLIST]: 'playlist',
  };
  return t(`book_player.label_${suffixMap[item] ?? 'table_of_contents'}`);
};

const resetSearch = () => (search.value = '');

const isPlaylistTab = computed(() => {
  return filteredNavItems.value[tab.value] === NAVIGATION_ITEMS.PLAYLIST;
});
</script>

<template>
  <div :class="{ 'pa-5': isMobile && isAudioOnly }">
    <template v-if="navigationItems">
      <div v-if="!isMobile || !isAudioOnly" class="nav-top-bar my-2">
        <v-toolbar-title>{{ t('book_player.label_navigation') }}</v-toolbar-title>
        <v-btn
          v-if="isTocBottom || isAudioOnly"
          size="48"
          variant="text"
          :title="t('label_close')"
          data-testid="bp-navigation-close-btn"
          @click="emit('closeTableOfContent')"
        >
          <FontAwesomeIcon icon="fas fa-chevron-left" />
        </v-btn>
      </div>
      <v-tabs
        v-model="tab"
        data-testid="bp-navigation-tabs"
        bg-color="primary-container"
        color="on-primary-container"
        grow
        hide-slider
        height="40"
        class="rounded text-on-primary-container"
        :class="{ 'mb-4': isPlaylistTab }"
        selected-class="bg-primary text-on-primary"
      >
        <v-tab
          v-for="item in filteredNavItems"
          :key="item"
          :data-testid="`bp-navigation-tab-${item}`"
          :text="getTabTitle(item)"
          rounded="sm"
        />
      </v-tabs>

      <v-text-field
        v-if="!isPlaylistTab"
        v-model="search"
        data-testid="bp-navigation-search"
        prepend-inner-icon="mdi-magnify"
        :label="t('book_player.label_search')"
        :aria-label="t('book_player.label_search')"
        density="compact"
        variant="outlined"
        class="my-2"
        hide-details
      />
    </template>
    <slot :tab="tab" :search="search" :reset-search="resetSearch" :navigation-items="filteredNavItems" />
  </div>
</template>

<style scoped lang="scss">
.nav-top-bar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;

  .v-toolbar-title {
    font-size: 20px;
    font-family: 'Filson-Soft', sans-serif;
  }
}
</style>
